import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import ImpersonateRequest from "../../entities/ImpersonateRequest";
import User, { UserType } from "../../entities/User";
import i18n from "../../i18n";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import SliceSingletonBase from "../types/SliceSingletonBase";
import { AppDispatch } from "../configureStore";

// RESPONSE DECLARATIONS //
interface UserResponse {
  CompanyId: number;
  UserType: UserType;
  SecurityToken: string;
  TenantId: number;
  UserGuid: string;
  UserId: string;
  WorkerId: number;
}

// SLICE //
interface State extends SliceSingletonBase<User> {}

const initialState: State = {
  data: null,
  loading: false,
  lastFetch: null,
  error: null
};

const slice = createSlice({
  name: "adminActions",
  initialState,
  reducers: {
    //IMPERSONATE
    impersonatedUserRequested: adminActions => {
      adminActions.loading = true;
      adminActions.error = null;
    },

    impersonatedUserReceived: (
      adminActions,
      action: PayloadAction<UserResponse>
    ) => {
      adminActions.data = schema(action.payload);
      adminActions.loading = false;
      adminActions.lastFetch = Date.now();
    },

    impersonatedUserRequestFailed: (
      adminActions,
      action: PayloadAction<ApiCallFailedPayload>
    ) => {
      adminActions.loading = false;
      adminActions.error = action.payload;
      adminActions.lastFetch = Date.now();
      adminActions.data = null;
    }
  }
});

const {
  impersonatedUserRequested,
  impersonatedUserReceived,
  impersonatedUserRequestFailed
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
export const postImpersonateRequest =
  (request: ImpersonateRequest) => async (dispatch: AppDispatch) => {
    if (!request) {
      return;
    }

    const result = await dispatch(
      apiCallBegan({
        url: "/api/Authentication/Impersonate",
        method: "post",
        headers: {
          "Accept-Language": i18n.language
        },
        data: request,
        onStart: impersonatedUserRequested.type,
        onSuccess: impersonatedUserReceived.type,
        onError: impersonatedUserRequestFailed.type
      })
    );
    return result;
  };

// NORMALIZATION //
const schema = (payload: UserResponse): User | null => {
  if (!payload) {
    return null;
  }
  return {
    CompanyId: payload.CompanyId,
    UserType: payload.UserType,
    Id: payload.UserId,
    TenantId: payload.TenantId,
    SecurityToken: payload.SecurityToken,
    UserGuid: payload.UserGuid,
    WorkerId: payload.WorkerId
  };
};
